import { AdminEmailEditor } from '../../../components/admin/AdminEmailEditor';
import { deliveryLabel } from "../../../lib/communicationRequests";
import { WelcomeEmailStatus } from "../../../components/admin/WelcomeEmailStatus";
import { CrmTemplatePicker } from "../crm/CrmTemplatePicker";
import { CrmTemplatesModal } from "../crm/CrmTemplatesModal";
import { TeamSignaturePreview } from "../accounts/TeamSignaturePanel";
import { AIDraft } from "../onboarding/AIDraft";
import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Sheet,
  Tab,
  TabList,
  Tabs,
  Textarea,
  Typography,
} from "@mui/joy";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { OrganizationProfileAPI as API } from "../../../lib/apiOrganizationProfile";
import { getApiErrorMessage } from "../../../lib/apiError";
import { MailboxConnection } from "../../../components/admin/MailboxConnection";
import { useProfileDraft } from "./ProfileDrafts";
import type { AdminOrganizationDetail } from "../../../types/admin";

export function OrganizationTimeline({
  org,
  doNotEmail,
  initialEmail = "",
}: {
  org: AdminOrganizationDetail;
  doNotEmail: boolean;
  initialEmail?: string;
}) {
  const cache = useQueryClient();
  const [mode, setMode] = useState(initialEmail ? "email" : "note");
  const [filter, setFilter] = useState("");
  const [body, setBody] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [subject, setSubject] = useState("");
  const [recipient, setRecipient] = useState(
    initialEmail || org.owner_email || org.company_email,
  );
  const [textRecipient, setTextRecipient] = useState<string | null>(null);
  const [when, setWhen] = useState("");
  const [notice, setNotice] = useState("");
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [templateRecipient, setTemplateRecipient] = useState("");
  const key = ["admin", "org-timeline", org.id];
  const history = useInfiniteQuery({
    queryKey: [...key, filter],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => API.timeline(org.id, filter, pageParam),
    getNextPageParam: (last, pages) =>
      last.next ? pages.length + 1 : undefined,
  });
  const textOptions = useQuery({
    queryKey: ["admin", "org-text", org.id, textRecipient],
    queryFn: () => API.textOptions(org.id, textRecipient ?? undefined),
    enabled: mode === "sms",
  });
  const deliveryNumber = textRecipient ?? textOptions.data?.to_number ?? "";
  const refresh = () => cache.invalidateQueries({ queryKey: key });
  const send = useMutation({
    mutationFn: async () => {
      if (mode === "email")
        await API.email(org.id, { to: recipient.trim(), subject, body, ...(bodyHtml ? { body_html: bodyHtml } : {}) });
      else if (mode === "sms")
        return await API.text(
          org.id,
          textOptions.data?.senders[0]?.number || "",
          body,
          deliveryNumber,
        );
      else
        await API.log(org.id, {
          kind: mode,
          summary: body.trim().slice(0, 255),
          body: body.trim(),
          ...(when ? { occurred_at: new Date(when).toISOString() } : {}),
        });
    },
    onSuccess: (result) => {
      setBody("");
      setBodyHtml("");
      setSubject("");
      setTemplateRecipient("");
      setWhen("");
      setNotice(
        mode === "email"
          ? "Email sent."
          : mode === "sms"
            ? result?.detail ||
              `Text: ${deliveryLabel(result?.status || "queued")}`
            : "Activity saved.",
      );
      refresh();
    },
  });
  useProfileDraft(
    "timeline",
    Boolean(
      body ||
        subject ||
        when ||
        textRecipient !== null ||
        recipient !== (initialEmail || org.owner_email || org.company_email),
    ),
    send.isPending || templateBusy,
  );
  const rows = history.data?.pages.flatMap((p) => p.results) ?? [];
  const emailAddresses = [
    ...new Set([org.owner_email, org.company_email].filter(Boolean)),
  ];
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          lg: "minmax(290px, 0.8fr) minmax(0, 1.2fr)",
        },
        gap: 2,
        alignItems: "start",
      }}
    >
      <Sheet
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: "lg",
          display: "grid",
          gap: 1.5,
          minWidth: 0,
        }}
      >
        <Typography level="title-lg">Start a conversation</Typography>
        <Typography level="body-sm">
          Messages and activity are shared with your CX team.
        </Typography>
        <Tabs
          value={mode}
          onChange={(_, v) => {
            if (v && !(send.isPending || templateBusy)) {
              setMode(String(v));
              send.reset();
              setNotice("");
            }
          }}
        >
          <TabList aria-label="Communication type" sx={{ flexWrap: "wrap" }}>
            {[
              ["note", "Note"],
              ["call", "Call"],
              ["meeting", "Meeting"],
              ["email", "Email"],
              ["sms", "Text"],
            ].map(([v, label]) => (
              <Tab key={v} value={v} disabled={send.isPending || templateBusy}>
                {label}
              </Tab>
            ))}
          </TabList>
        </Tabs>
        {mode === "email" && (
          <>
            <MailboxConnection compact />
            {doNotEmail && (
              <Alert color="warning">
                This customer is marked Do not email.
              </Alert>
            )}
            <FormControl>
              <FormLabel>To</FormLabel>
              <Input
                type="email"
                disabled={send.isPending || templateBusy}
                value={recipient || ""}
                onChange={(e) => setRecipient(e.target.value)}
                slotProps={{ input: { list: "organization-email-defaults" } }}
              />
              <datalist id="organization-email-defaults">
                {emailAddresses.map((address) => (
                  <option key={address} value={address} />
                ))}
              </datalist>
              <Typography level="body-xs">
                Used for this message only. Saved contact details stay
                unchanged.
              </Typography>
            </FormControl>
            <CrmTemplatePicker
              organizationId={org.id}
              recipient={recipient || ""}
              hasDraft={!!(subject || body)}
              disabled={
                send.isPending ||
                templateBusy ||
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((recipient || "").trim())
              }
              onBusyChange={setTemplateBusy}
              onManage={() => setTemplatesOpen(true)}
              onApply={(preview) => {
                setSubject(preview.subject);
                setBody(preview.body);
                setBodyHtml(preview.body_html || "");
                setTemplateRecipient((recipient || "").trim());
                setNotice("");
                send.reset();
              }}
            />
            {templateRecipient &&
              templateRecipient !== (recipient || "").trim() && (
                <Alert color="warning">
                  The recipient changed after applying a template. Choose the
                  template again to update the name, or review your draft before
                  sending.
                </Alert>
              )}
            <FormControl required>
              <FormLabel>Subject</FormLabel>
              <Input
                disabled={send.isPending || templateBusy}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                slotProps={{ input: { maxLength: 500 } }}
              />
            </FormControl>
          </>
        )}
        {mode === "sms" && (
          <>
            {textOptions.isPending && (
              <Typography role="status">Loading texting options…</Typography>
            )}
            {textOptions.isError && (
              <Alert
                color="danger"
                endDecorator={
                  <Button onClick={() => textOptions.refetch()}>Retry</Button>
                }
              >
                Texting options could not be loaded.
              </Alert>
            )}
            <FormControl required>
              <FormLabel>Recipient phone number</FormLabel>
              <Input
                type="tel"
                value={deliveryNumber}
                disabled={
                  send.isPending || templateBusy
                }
                onChange={(e) => setTextRecipient(e.target.value)}
              />
              <Typography level="body-xs">
                Used for this text only. You can enter your own number to test; the account’s saved number stays unchanged.
              </Typography>
            </FormControl>
            {textOptions.data && (
              <>
                <FormControl>
                  <FormLabel>From ApexOS</FormLabel>
                  <Input
                    readOnly
                    value={textOptions.data.senders[0]?.number || "Not configured"}
                  />
                </FormControl>
                {!textOptions.data.senders.length && (
                  <Alert color="warning">
                    {textOptions.data.sender_detail || "The shared ApexOS number is not configured. Contact the platform team."}
                  </Alert>
                )}
                <Typography level="body-sm">
                  Staff messages always use the shared ApexOS number. Your customer’s
                  texting registration and usage balance do not affect these messages.
                </Typography>
                <Typography level="body-xs">
                  ApexOS identification and STOP instructions are included automatically.
                  Replies appear in the account timeline and notify the CSM.
                </Typography>
                {textOptions.data.permission_detail && (
                  <Alert color="warning">{textOptions.data.permission_detail}</Alert>
                )}
              </>
            )}
          </>
        )}
        {!["sms", "email"].includes(mode) && (
          <FormControl>
            <FormLabel>When it happened (optional)</FormLabel>
            <Input
              disabled={send.isPending || templateBusy}
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
          </FormControl>
        )}
        {["email", "sms"].includes(mode) && (
          <AIDraft
            key={`${mode}:${recipient}:${deliveryNumber}`}
            input={{ organization_id: org.id, ...(mode === "email" ? { recipient: recipient || "" } : { to_number: deliveryNumber }), channel: mode as "email" | "sms", purpose: "Help with the next onboarding step", mode: "onboarding", subject, body }}
            disabled={send.isPending || templateBusy || (mode === "email" && (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((recipient || "").trim()) || doNotEmail))}
            onBusyChange={setTemplateBusy}
            onApply={(draft) => { setSubject(draft.subject); setBody(draft.body); setBodyHtml(""); setTemplateRecipient(mode === "email" ? (recipient || "").trim() : ""); setNotice(""); send.reset(); }}
          />
        )}
        <FormControl required>
          <FormLabel>
            {["sms", "email"].includes(mode) ? "Message" : "What happened?"}
          </FormLabel>
          {mode === 'email' ? <AdminEmailEditor label="Message" text={body} html={bodyHtml} includeSignature
            disabled={send.isPending || templateBusy} onChange={(html, text) => { setBody(text); setBodyHtml(html); }} /> : (
          <Textarea
            minRows={5}
            value={body}
            onChange={(e) => { setBody(e.target.value); setBodyHtml(""); }}
            slotProps={{
              textarea: { maxLength: mode === "sms" ? 1600 : 20000 },
            }}
            disabled={send.isPending || templateBusy}
          />
          )}
        </FormControl>
        {mode === "email" && <TeamSignaturePreview />}
        {mode === "sms" && (
          <Typography level="body-xs">
            {body.length}/1,600 characters
          </Typography>
        )}
        {send.error && (
          <Alert color="danger">
            {getApiErrorMessage(
              send.error,
              "This could not be saved. Your draft is still here.",
            )}
          </Alert>
        )}
        {notice && (
          <Alert color="success" role="status">
            {notice}
          </Alert>
        )}
        <Button
          loading={send.isPending}
          disabled={
            templateBusy ||
            !body.trim() ||
            (mode === "email" &&
              (!subject.trim() ||
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((recipient || "").trim()) ||
                doNotEmail)) ||
            (mode === "sms" &&
              (!textOptions.data?.allowed ||
                !textOptions.data.senders.length ||
                textOptions.isError))
          }
          onClick={() => send.mutate()}
        >
          {mode === "email"
            ? "Send email"
            : mode === "sms"
              ? "Send text"
              : "Save activity"}
        </Button>
      </Sheet>
      <Box sx={{ display: "grid", gap: 1.5, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography level="title-lg" sx={{ flex: 1 }}>
            Client timeline
          </Typography>
          <Button size="sm" variant="plain" onClick={refresh}>
            Refresh history
          </Button>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {[
            ["", "All activity"],
            ["notes", "Notes"],
            ["calls", "Calls & meetings"],
            ["emails", "Emails"],
            ["sms", "Texts"],
            ["support", "Support"],
          ].map(([v, label]) => (
            <Button
              size="sm"
              key={v}
              variant={filter === v ? "solid" : "outlined"}
              color={filter === v ? "primary" : "neutral"}
              onClick={() => setFilter(v)}
            >
              {label}
            </Button>
          ))}
        </Box>
        {history.isPending && (
          <Typography role="status">Loading timeline…</Typography>
        )}
        {history.isError && (
          <Alert
            color="danger"
            endDecorator={
              <Button
                onClick={() =>
                  history.isFetchNextPageError
                    ? history.fetchNextPage()
                    : history.refetch()
                }
              >
                Retry
              </Button>
            }
          >
            Could not load activity.
          </Alert>
        )}
        {!history.isPending && !history.isError && !rows.length && (
          <Sheet variant="soft" sx={{ p: 3, borderRadius: "md" }}>
            <Typography level="title-sm">No activity here yet</Typography>
            <Typography level="body-sm">
              Log a note, call, or meeting to give the next teammate context.
            </Typography>
          </Sheet>
        )}
        {rows.map((row) => (
          <Sheet
            key={row.id}
            variant="outlined"
            sx={{ p: 1.5, borderRadius: "md", overflowWrap: "anywhere" }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Chip size="sm" variant="soft">
                {row.metadata.channel === "sms"
                  ? "Text"
                  : row.kind
                      .replace(/_/g, " ")
                      .replace(/^./, (letter) => letter.toUpperCase())}
              </Chip>
              <Typography level="body-xs">
                {new Date(row.occurred_at).toLocaleString()}
                {row.actor_name ? ` · ${row.actor_name}` : ""}
              </Typography>
            </Box>
            {(!row.body?.trim() ||
              !row.body.trim().startsWith(row.summary.trim())) && (
              <Typography level="title-sm" sx={{ mt: 1 }}>
                {row.summary}
              </Typography>
            )}
            {row.body && (
              <Typography
                level="body-sm"
                sx={{ mt: 1, whiteSpace: "pre-wrap" }}
              >
                {row.body}
              </Typography>
            )}
            {row.metadata.channel === "sms" && !!row.metadata.status && (
              <Chip
                size="sm"
                sx={{ mt: 1 }}
                color={
                  ["failed", "undelivered"].includes(
                    String(row.metadata.status),
                  )
                    ? "danger"
                    : row.metadata.status === "delivered"
                      ? "success"
                      : "neutral"
                }
              >
                {String(row.metadata.status).replace(/^./, (letter) =>
                  letter.toUpperCase(),
                )}
              </Chip>
            )}
            <WelcomeEmailStatus metadata={row.metadata} />
            {!!row.metadata.delivery_error && row.metadata.source !== "inquiry_welcome" && (
              <Typography level="body-sm" color="danger">
                {String(row.metadata.delivery_error)}
              </Typography>
            )}
            {row.support_ticket_detail && (
              <Chip
                size="sm"
                color={
                  row.support_ticket_detail.is_open ? "warning" : "success"
                }
                sx={{ mt: 1 }}
              >
                {row.support_ticket_detail.ticket_number} ·{" "}
                {row.support_ticket_detail.status.replace(/_/g, " ")}
              </Chip>
            )}
          </Sheet>
        ))}
        {history.hasNextPage && (
          <Button
            variant="outlined"
            loading={history.isFetchingNextPage}
            onClick={() => history.fetchNextPage()}
          >
            Load older activity
          </Button>
        )}
      </Box>
      {templatesOpen && (
        <CrmTemplatesModal onClose={() => setTemplatesOpen(false)} />
      )}
    </Box>
  );
}
